import { Injectable, signal, computed } from '@angular/core';

/** Estado de salud del sistema. */
export type HealthStatus = 'healthy' | 'degraded' | 'critical';

/** Registro de error de un endpoint. */
export interface EndpointError {
  url: string;
  status: number;
  timestamp: Date;
  message?: string;
}

/** Configuración del servicio de salud. */
const CONFIG = {
  /** Tiempo en ms para considerar un error como reciente. */
  errorWindowMs: 5 * 60 * 1000, // 5 minutos
  /** Cantidad de errores recientes para estado degradado. */
  degradedThreshold: 2,
  /** Cantidad de errores recientes para estado crítico. */
  criticalThreshold: 5,
  /** Máximo de errores a almacenar en memoria. */
  maxStoredErrors: 50,
};

/**
 * Servicio de monitoreo de salud de endpoints.
 *
 * Registra errores HTTP y determina el estado de salud del sistema
 * basándose en la cantidad de errores recientes.
 */
@Injectable({ providedIn: 'root' })
export class HealthService {
  private readonly errors = signal<EndpointError[]>([]);

  /** Errores recientes dentro de la ventana de tiempo configurada. */
  readonly recentErrors = computed(() => {
    const now = Date.now();
    return this.errors().filter(
      (error) => now - error.timestamp.getTime() < CONFIG.errorWindowMs
    );
  });

  /** Cantidad de errores recientes. */
  readonly errorCount = computed(() => this.recentErrors().length);

  /** Estado de salud actual del sistema. */
  readonly status = computed<HealthStatus>(() => {
    const count = this.errorCount();
    if (count >= CONFIG.criticalThreshold) return 'critical';
    if (count >= CONFIG.degradedThreshold) return 'degraded';
    return 'healthy';
  });

  /** Indica si el sistema tiene problemas (degradado o crítico). */
  readonly hasIssues = computed(() => this.status() !== 'healthy');

  /**
   * Registra un error de endpoint.
   *
   * @param url - URL del endpoint que falló
   * @param status - Código de estado HTTP
   * @param message - Mensaje de error opcional
   */
  registerError(url: string, status: number, message?: string): void {
    const error: EndpointError = {
      url,
      status,
      timestamp: new Date(),
      message,
    };

    this.errors.update((current) => {
      const updated = [error, ...current];
      // Limitar cantidad de errores almacenados
      if (updated.length > CONFIG.maxStoredErrors) {
        return updated.slice(0, CONFIG.maxStoredErrors);
      }
      return updated;
    });
  }

  /** Limpia todos los errores registrados. */
  clearErrors(): void {
    this.errors.set([]);
  }

  /**
   * Obtiene los errores agrupados por dominio/servicio.
   *
   * @returns Mapa con dominio como clave y cantidad de errores como valor
   */
  getErrorsByDomain(): Map<string, number> {
    const grouped = new Map<string, number>();

    for (const error of this.recentErrors()) {
      const domain = this.extractDomain(error.url);
      const current = grouped.get(domain) || 0;
      grouped.set(domain, current + 1);
    }

    return grouped;
  }

  private extractDomain(url: string): string {
    // Extraer el primer segmento significativo de la URL
    // Ej: "/api/reportes/getEstado" -> "reportes"
    const match = url.match(/\/(?:api\/)?([^/?]+)/);
    return match?.[1] || 'unknown';
  }
}
